import { DestroyRef, Component, signal, computed, input, output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ZendraLocalizationServices } from '../enums/abpLocalizationServices';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { ZnAlert } from '../zn-alert/zn-alert';
import { IModalContent } from '../zn-modal-wrapper/interfaces/IModalContent';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ExcelImportResultLike } from '../interfaces/dtos/excel-import.models';
import { ToasterService } from '@abp/ng.theme.shared';
import { localizeWithFallback } from '../utils/localization-utils';

@Component({
  selector: 'zn-file-uploader',
  standalone: true,
  imports: [CommonModule, LocalizationPipe, ZnAlert],
  templateUrl: './zn-file-uploader.html',
  styleUrl: './zn-file-uploader.scss'
})
export class ZnFileUploader implements IModalContent {
  private readonly destroyRef = inject(DestroyRef);
  protected toaster = inject(ToasterService);
  private readonly localizationService = inject(LocalizationService);
  
  accept = input<string[]>(['xls', 'xlsx']);
  maxSizeMb = input<number>(2);
  multiple = input<boolean>(false);
  localizationResource = input<string>(ZendraLocalizationServices.LanguageService);
  importFile = input<((formData: FormData) => Observable<ExcelImportResultLike<unknown>>) | undefined>();

  fileChange = output<File | null>();
  
  file = signal<File | null>(null);
  isDragOver = signal(false);
  error = signal<string | null>(null);
  loading = signal(false);
  defaultPrimaryText = signal(`${this.localizationResource()}::ZnFileUploaderPrimaryText`);
  defaultSecondaryText = signal(`${this.localizationResource()}::ZnFileUploaderSecondaryText`);
  dragOverText = signal( `${this.localizationResource()}::ZnFileUploaderDragOverText`);
  canSave = signal(false);

  private readonly activeModal = inject(NgbActiveModal);

  primaryText = computed(() =>
    this.isDragOver()
      ? this.dragOverText()
      : this.defaultPrimaryText()
  );

  secondaryText = computed(() =>
    this.isDragOver()
      ? ''
      : this.defaultSecondaryText()
  );

  acceptAttr = computed(() =>
    this.accept().map(ext => '.' + ext).join(',')
  );

  acceptText = computed(() => {
    const localizedOr = localizeWithFallback(
      this.localizationService,
      `${this.localizationResource()}::ZnFileUploaderOr`
    );

    return this.accept().map(ext => '.' + ext).join(localizedOr);
  });

  onDragOver(event: DragEvent) {
    if (this.loading()) return;
    event.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave() {
    this.isDragOver.set(false);
  }

  onDrop(event: DragEvent) {
    if (this.loading()) return;
    event.preventDefault();
    this.isDragOver.set(false);

    const droppedFile = event.dataTransfer?.files[0];
    if (droppedFile) {
      this.handleFile(droppedFile);
    }
  }

  onFileSelected(event: Event) {
    if (this.loading()) return;

    const input = event.target as HTMLInputElement;
    const selectedFile = input.files?.[0];

    if (selectedFile) {
      this.handleFile(selectedFile);
    }

    input.value = '';
  }

  handleFile(file: File) {
    this.error.set(null);

    const extension = file.name.split('.').pop()?.toLowerCase();
    const maxSizeBytes = this.maxSizeMb() * 1024 * 1024;

    if (!extension || !this.accept().includes(extension)) {
      this.error.set(
        `${localizeWithFallback(
          this.localizationService,
          `${this.localizationResource()}::ZnFileUploaderErrorFormat`
        )} ${this.acceptText()}`.trim()
      );
      this.canSave.set(false);
      return;
    }

    if (file.size > maxSizeBytes) {
      this.error.set(
        `${localizeWithFallback(
          this.localizationService,
          `${this.localizationResource()}::ZnFileUploaderErrorSize`
        )} ${this.maxSizeMb()} MB`.trim()
      );
      this.canSave.set(false);
      return;
    }

    this.file.set(file);
    this.fileChange.emit(file);
    this.canSave.set(true);
  }

  removeFile() {
    if (this.loading()) return;
    this.file.set(null);
    this.fileChange.emit(null);
    this.canSave.set(false);
  }

  confirm(): void {
    if (this.loading()) {
      return;
    }

    const selectedFile = this.file();
    const importFile = this.importFile();

    if (!selectedFile) {
      this.error.set(
        localizeWithFallback(
          this.localizationService,
          `${this.localizationResource()}::ZnFileUploaderErrorNoFileSelected`
        )
      );
      this.canSave.set(false);
      return;
    }

    if (!importFile) {
      this.error.set(
        localizeWithFallback(
          this.localizationService,
          `${this.localizationResource()}::ZnFileUploaderErrorUploadCallbackNotConfigured`
        )
      );
      this.canSave.set(false);
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile, selectedFile.name);

    this.error.set(null);
    this.loading.set(true);
    this.canSave.set(false);

    importFile(formData).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: result => {
        this.activeModal.close({
          ...result,
          fileName: selectedFile.name,
        });
        
        if(!result.hasErrors) {
          
          this.toaster.success(
          `${this.localizationResource()}::MessageSaveSuccess`,
          '',
          { life: 5000 }
        );
        }
      },
      error: err => {
        const errorMessage = this.getErrorMessage(err);
        this.error.set(errorMessage);
      }
    }).add(() => {
      this.loading.set(false);
      this.canSave.set(this.file() !== null);
    });
  }

  dismiss(): void {
    if (this.loading()) return;
    this.activeModal.dismiss();
  }

  private getErrorMessage(err: unknown): string {
    const error = err as {
      error?: { error?: { message?: string }; message?: string };
      message?: string;
    };

    return error?.error?.error?.message
      ?? error?.error?.message
      ?? error?.message
      ?? localizeWithFallback(
        this.localizationService,
        `${this.localizationResource()}::ZnFileUploaderErrorUnexpectedUpload`
      );
  }

}
